// Small shared primitives. Deliberately thin: the visual language lives in oreia.css, so these
// components exist to stop the same markup and the same aria wiring being retyped per screen.

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="orow" style={{ gap: 10 }}>
      <span className="ospin" role="progressbar" aria-label={label ?? 'Loading'} />
      {label ? <span className="otext-mute" style={{ fontSize: 13.5 }}>{label}</span> : null}
    </span>
  );
}

export function FullPageSpinner({ label }: { label?: string }) {
  return (
    <div className="ocenter">
      <Spinner label={label} />
    </div>
  );
}

export function Notice({
  children,
  tone = 'info',
}: {
  children: ReactNode;
  tone?: 'info' | 'danger';
}) {
  return (
    <p className={`onotice${tone === 'danger' ? ' danger' : ''}`} role={tone === 'danger' ? 'alert' : undefined}>
      {children}
    </p>
  );
}

interface PrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  children: ReactNode;
}

export function PrimaryButton({ loading, children, disabled, ...rest }: PrimaryButtonProps) {
  return (
    <button className="octa" disabled={disabled || loading} {...rest}>
      {loading ? <span className="ospin" style={{ width: 16, height: 16 }} aria-hidden /> : null}
      {children}
    </button>
  );
}

export function GhostButton({ children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      style={{
        background: 'var(--glass-light-fill)',
        border: '1px solid var(--glass-light-border)',
        color: 'var(--ink-soft)',
        borderRadius: 'var(--r-pill)',
        padding: '11px 18px',
        fontSize: 13.5,
        cursor: 'pointer',
        ...rest.style,
      }}
    >
      {children}
    </button>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | null;
}

export function Field({ label, hint, error, id, ...rest }: FieldProps) {
  const inputId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="ostack" style={{ gap: 7 }}>
      <label className="olabel" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className="oinput"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {error ? (
        <span id={`${inputId}-error`} style={{ color: 'var(--danger)', fontSize: 12.5 }} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} className="otext-mute" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
}

export function TextAreaField({ label, hint, id, ...rest }: TextAreaFieldProps) {
  const inputId = id ?? `t-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="ostack" style={{ gap: 7 }}>
      <label className="olabel" htmlFor={inputId}>
        {label}
      </label>
      <textarea id={inputId} className="oinput" rows={4} {...rest} />
      {hint ? (
        <span className="otext-mute" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** Section label used above card groups, matching the phone layout's rhythm. */
export function SectionLabel({ children, ...rest }: { children: ReactNode } & { style?: React.CSSProperties }) {
  return (
    <div className="olabel" style={{ display: 'block', margin: '22px 0 12px 2px', ...rest.style }}>
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="ocard ocard-light" style={{ textAlign: 'center', padding: '32px 22px' }}>
      <h3 className="oserif" style={{ fontSize: 24, margin: '0 0 8px' }}>
        {title}
      </h3>
      <p className="otext-soft" style={{ fontSize: 14, lineHeight: 1.55, margin: '0 auto', maxWidth: '34ch' }}>
        {body}
      </p>
      {action ? <div style={{ marginTop: 18 }}>{action}</div> : null}
    </div>
  );
}
